/**
 * Custom hook for fetching categories and category groups
 * Extracted from Activity.tsx to improve code organization
 */

import { useEffect, useState } from "react";
import {
  collection,
  query,
  where,
  onSnapshot,
  Unsubscribe,
  FirestoreError,
} from "firebase/firestore";
import { db } from "../../../firebase";
import { TransactionCategory, TransactionCategoryGroup } from "../../../types";
import { isDemoMode } from "../../../utils/demoMode";
import { isBypassAuthEnabled } from "../../../utils/bypassAuth";
import { api } from "../../../services/api";

export interface UseCategoriesResult {
  expenseCategories: TransactionCategory[];
  incomeCategories: TransactionCategory[];
  categoryGroups: TransactionCategoryGroup[];
}

export interface UseCategoriesParams {
  workspaceId: string;
}

// Cache for categories per workspace - persists across component unmounts
const categoriesCache = new Map<string, {
  expenseCategories: TransactionCategory[];
  incomeCategories: TransactionCategory[];
  categoryGroups: TransactionCategoryGroup[];
  timestamp: number;
}>();

// Cache expiration time: 5 minutes
const CACHE_EXPIRY_MS = 5 * 60 * 1000;

/**
 * Hook for fetching categories and groups for expense/income transactions using real-time listeners
 * Uses caching to avoid reloading data when navigating back to Activity
 */
export const useCategories = ({ workspaceId }: UseCategoriesParams): UseCategoriesResult => {
  // Check cache first
  const cachedData = categoriesCache.get(workspaceId);
  const hasValidCache = !!(cachedData && (Date.now() - cachedData.timestamp) < CACHE_EXPIRY_MS);
  
  const [expenseCategories, setExpenseCategories] = useState<TransactionCategory[]>(
    hasValidCache && cachedData ? cachedData.expenseCategories : []
  );
  const [incomeCategories, setIncomeCategories] = useState<TransactionCategory[]>(
    hasValidCache && cachedData ? cachedData.incomeCategories : []
  );
  const [categoryGroups, setCategoryGroups] = useState<TransactionCategoryGroup[]>(
    hasValidCache && cachedData ? cachedData.categoryGroups : []
  );

  useEffect(() => {
    // Allow loading in demo mode (read-only access to demo workspace)
    if (!workspaceId) {
      setExpenseCategories([]);
      setIncomeCategories([]);
      setCategoryGroups([]);
      return;
    }

    // Use Firestore onSnapshot for real-time updates (works for both bypass auth and authenticated users)
    const categoriesRef = collection(db, "workspaces", workspaceId, "categories");
    const groupsRef = collection(db, "workspaces", workspaceId, "categoryGroups");
    
    // Real-time listener for expense categories
    const expenseCategoriesQuery = query(categoriesRef, where("type", "==", "expense"));
    const unsubscribeExpenseCategories = onSnapshot(
      expenseCategoriesQuery,
      (snapshot) => {
        const expenseCats = snapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            name: data.name as string,
            type: data.type as "expense" | "income",
            groupId: data.groupId as string,
            enabledDetails: data.enabledDetails as string[] | undefined,
            defaultValues: data.defaultValues as Record<string, string | number> | undefined,
            createdAt: data.createdAt,
            updatedAt: data.updatedAt,
            order: data.order as number | undefined,
          } as TransactionCategory;
        });
        
        // Update cache
        const currentCache = categoriesCache.get(workspaceId);
        categoriesCache.set(workspaceId, {
          expenseCategories: expenseCats,
          incomeCategories: currentCache?.incomeCategories || [],
          categoryGroups: currentCache?.categoryGroups || [],
          timestamp: Date.now(),
        });
        
        setExpenseCategories(expenseCats);
        console.log("🔄 [Desktop] Expense categories updated:", expenseCats.length, "at", new Date().toISOString());
        // Log default values for debugging - show ALL categories and their defaults
        expenseCats.forEach(cat => {
          console.log(`📋 [Desktop] Category "${cat.name}" (id: ${cat.id}):`, {
            defaultValues: cat.defaultValues,
            updatedAt: cat.updatedAt,
          });
        });
      },
      (error: FirestoreError) => {
        console.error("Error fetching expense categories:", error);
        // Fallback to API if Firestore fails (for authenticated users)
        if (!isBypassAuthEnabled()) {
          api.getCategories(workspaceId, "expense")
            .then((data) => {
              setExpenseCategories(data);
            })
            .catch((err) => {
              console.error("API fallback also failed:", err);
              setExpenseCategories([]);
            });
        } else {
          setExpenseCategories([]);
        }
      }
    );

    // Real-time listener for income categories
    const incomeCategoriesQuery = query(categoriesRef, where("type", "==", "income"));
    const unsubscribeIncomeCategories = onSnapshot(
      incomeCategoriesQuery,
      (snapshot) => {
        const incomeCats = snapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            name: data.name as string,
            type: data.type as "expense" | "income",
            groupId: data.groupId as string,
            enabledDetails: data.enabledDetails as string[] | undefined,
            defaultValues: data.defaultValues as Record<string, string | number> | undefined,
            createdAt: data.createdAt,
            updatedAt: data.updatedAt,
            order: data.order as number | undefined,
          } as TransactionCategory;
        });
        
        // Update cache
        const currentCache = categoriesCache.get(workspaceId);
        categoriesCache.set(workspaceId, {
          expenseCategories: currentCache?.expenseCategories || [],
          incomeCategories: incomeCats,
          categoryGroups: currentCache?.categoryGroups || [],
          timestamp: Date.now(),
        });
        
        setIncomeCategories(incomeCats);
        console.log("🔄 [Desktop] Income categories updated:", incomeCats.length, "at", new Date().toISOString());
        // Log default values for debugging - show ALL categories and their defaults
        incomeCats.forEach(cat => {
          console.log(`📋 [Desktop] Category "${cat.name}" (id: ${cat.id}):`, {
            defaultValues: cat.defaultValues,
            updatedAt: cat.updatedAt,
          });
        });
      },
      (error: FirestoreError) => {
        console.error("Error fetching income categories:", error);
        // Fallback to API if Firestore fails (for authenticated users)
        if (!isBypassAuthEnabled()) {
          api.getCategories(workspaceId, "income")
            .then((data) => {
              setIncomeCategories(data);
            })
            .catch((err) => {
              console.error("API fallback also failed:", err);
              setIncomeCategories([]);
            });
        } else {
          setIncomeCategories([]);
        }
      }
    );

    // Real-time listener for category groups
    const unsubscribeGroups = onSnapshot(
      groupsRef,
      (snapshot) => {
        const groups = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        } as TransactionCategoryGroup));
        
        // Update cache
        const currentCache = categoriesCache.get(workspaceId);
        categoriesCache.set(workspaceId, {
          expenseCategories: currentCache?.expenseCategories || [],
          incomeCategories: currentCache?.incomeCategories || [],
          categoryGroups: groups,
          timestamp: Date.now(),
        });
        
        setCategoryGroups(groups);
        console.log("🔄 Category groups updated:", groups.length);
      },
      (error: FirestoreError) => {
        console.error("Error fetching category groups:", error);
        // Fallback to API if Firestore fails (for authenticated users)
        if (!isBypassAuthEnabled()) {
          api.getGroups(workspaceId)
            .then((data) => {
              setCategoryGroups(data);
            })
            .catch((err) => {
              console.error("API fallback also failed:", err);
              setCategoryGroups([]);
            });
        } else {
          setCategoryGroups([]);
        }
      }
    );

    return () => {
      unsubscribeExpenseCategories();
      unsubscribeIncomeCategories();
      unsubscribeGroups();
    };
  }, [workspaceId]);

  return {
    expenseCategories,
    incomeCategories,
    categoryGroups,
  };
};
